import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import FranchiseInfoNote from '../components/FranchiseInfoNote';

const SummaryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const {
    vehicleData,
    abonnement,
    franchise,
    monthlyPrice,
    firstRegistrationDate,
    mileage,
    plateNumber,
    commitmentMonths,
    customer,
    acceptsTerms,
    acceptsContract,
    sellerCode,
  } = location.state || {};
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Redirect if no data
  useEffect(() => {
    if (!vehicleData || !customer) {
      navigate('/abonnements');
    }
  }, [vehicleData, customer, navigate]);
  
  const handleContinue = () => {
    setLoading(true);
    setError('');
    navigate('/subscription/payment-method', {
      state: {
        vehicleData,
        abonnement,
        franchise,
        monthlyPrice,
        firstRegistrationDate,
        mileage,
        plateNumber,
        commitmentMonths,
        customer,
        acceptsTerms,
        acceptsContract,
        sellerCode,
      }
    });
  };
  
  if (!vehicleData || !customer) return null;
  
  const totalCommitment = monthlyPrice * commitmentMonths;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
      {/* Hero Section */}
      <section className="pt-20 py-16 bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Souscription à votre abonnement
          </h1>
          <p className="text-lg text-gray-600">
            Étape 3 sur 3 : Récapitulatif et paiement
          </p>
        </div>
      </section>

      <div className="container max-w-4xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        <div className="flex items-center justify-center mb-8 px-2">
          <div className="flex items-center w-full max-w-md gap-1 sm:gap-2">
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#52a675] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                ✓
              </div>
              <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium text-gray-900 truncate">Véhicule</span>
            </div>
            <div className="flex-1 h-1 bg-[#52a675] min-w-[8px]"></div>
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#52a675] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                ✓
              </div>
              <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium text-gray-900 truncate">Client</span>
            </div>
            <div className="flex-1 h-1 bg-[#52a675] min-w-[8px]"></div>
            <div className="flex items-center min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#52a675] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                3
              </div>
              <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium text-gray-900 truncate">Récap.</span>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="space-y-6 mb-8">
          {/* Subscription Summary */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="h-5 w-5 text-[#52a675] mr-2" />
                Abonnement sélectionné
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Type</p>
                  <p className="font-semibold">Abonnement {abonnement}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Prix mensuel</p>
                  <p className="font-semibold text-[#52a675]">CHF {monthlyPrice?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Franchise</p>
                  <p className="font-semibold">{franchise} CHF</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Engagement</p>
                  <p className="font-semibold">{commitmentMonths} mois</p>
                </div>
              </div>
              <div className="mt-4">
                <FranchiseInfoNote abonnement={abonnement} />
              </div>
            </CardContent>
          </Card>

          {/* Vehicle Summary */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="h-5 w-5 text-[#52a675] mr-2" />
                Véhicule
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Marque et modèle</p>
                  <p className="font-semibold">{vehicleData.brand} {vehicleData.model || ''}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Année</p>
                  <p className="font-semibold">{vehicleData.year}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Immatriculation</p>
                  <p className="font-semibold">{plateNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">1ère mise en circulation</p>
                  <p className="font-semibold">{new Date(firstRegistrationDate).toLocaleDateString('fr-CH')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Kilométrage</p>
                  <p className="font-semibold">{mileage.toLocaleString()} km</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Motorisation</p>
                  <p className="font-semibold">{vehicleData.displacement_cc} cc | {vehicleData.power_ch} CH</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Summary */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="h-5 w-5 text-[#52a675] mr-2" />
                Vos informations
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Nom complet</p>
                  <p className="font-semibold">{customer.first_name} {customer.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold">{customer.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Téléphone</p>
                  <p className="font-semibold">{customer.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Adresse</p>
                  <p className="font-semibold">{customer.address}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Localité</p>
                  <p className="font-semibold">{customer.zip_code} {customer.city}, {customer.country}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Summary */}
          <Card className="bg-gradient-to-br from-[#52a675] to-[#4a9566] text-white">
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-lg mb-2">Tarif mensuel</p>
                <p className="text-4xl font-bold mb-2">CHF {monthlyPrice?.toFixed(2)}</p>
                <p className="text-sm opacity-90">
                  {abonnement === 'S' ? 'Minimum 12 mois' : 'Minimum 24 mois'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Important Info */}
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Important :</strong> En cliquant sur "Confirmer", vous acceptez le contrat d'abonnement.
            Vous recevrez immédiatement votre facture et votre contrat par email.
          </AlertDescription>
        </Alert>

        {/* Error Message */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={loading}
            className="flex-1"
          >
            Retour
          </Button>
          <Button
            onClick={handleContinue}
            disabled={loading}
            className="flex-1 bg-[#52a675] hover:bg-[#4a9566] text-lg py-6"
            data-testid="summary-continue-payment-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Préparation...
              </>
            ) : (
              'Choisir la méthode de paiement'
            )}
          </Button>
        </div>

        <p className="text-center text-sm text-gray-500 mt-4">
          Vous pourrez choisir entre paiement par carte ou par facture QR
        </p>
      </div>
    </div>
  );
};

export default SummaryPage;
